/**
 * EditableSection
 *
 * Reusable component for inline-editable sections in automation cards.
 * Provides consistent edit/view toggle behavior matching v2 pattern.
 *
 * Usage:
 * <EditableSection
 *   label="What?"
 *   summary={<PlanWhatSummary automation={automation} />}
 *   editor={<PlanWhatSection automation={automation} index={index} />}
 * />
 */

import { faPencil } from '@fortawesome/pro-regular-svg-icons';
import { FC, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Icon } from 'src/components/Icon';
import { IconButton } from 'src/components/IconButton';
import { Typography } from 'src/components/Typography';
import { disabledAutomationColors } from 'src/pages/Automations/automationDragStyles';

export interface EditableSectionProps {
  /** Section label (e.g., "When?", "What?") */
  label: string;
  /** Content shown in view mode */
  summary: ReactNode;
  /** Content shown in edit mode */
  editor: ReactNode;
  /** Whether editing is allowed (shows/hides pencil icon) */
  editable?: boolean;
  /** Initial editing state for uncontrolled mode (default: false) */
  defaultEditing?: boolean;
  /** Control editing state externally (optional) */
  editing?: boolean;
  /** Callback when editing starts (optional) */
  onEditStart?: () => void;
  /** Callback when editing ends (optional) */
  onEditEnd?: () => void;
  /** Muted styling for disabled automations */
  muted?: boolean;
}

export const EditableSection: FC<EditableSectionProps> = ({
  label,
  summary,
  editor,
  editable = true,
  defaultEditing = false,
  editing: controlledEditing,
  onEditStart,
  onEditEnd,
  muted = false,
}) => {
  const { t } = useTranslation();
  const [internalEditing, setInternalEditing] = useState(defaultEditing);

  // Support both controlled and uncontrolled modes
  const isEditing = controlledEditing ?? internalEditing;

  const handleEditStart = () => {
    setInternalEditing(true);
    onEditStart?.();
  };

  const handleEditEnd = () => {
    setInternalEditing(false);
    onEditEnd?.();
  };

  return (
    <Box display="flex" gap={1} alignItems="baseline">
      <Box sx={{ width: 80, flexShrink: 0 }}>
        <Typography
          color={muted ? disabledAutomationColors.label : undefined}
          fontWeight="bold"
          variant="body2"
        >
          {label}
        </Typography>
      </Box>
      <Box flex={1}>
        {isEditing ? (
          <Box>
            {editor}
            <Button
              onClick={handleEditEnd}
              size="small"
              sx={{ mt: 1, px: 1.5 }}
              variant="borderless"
            >
              {t('automations.common.done')}
            </Button>
          </Box>
        ) : (
          <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{
              '& .section-edit-button': {
                opacity: 0,
                transition: 'opacity 0.15s ease-in-out',
              },
              '&:hover .section-edit-button, &:focus-within .section-edit-button': {
                opacity: 1,
              },
            }}
          >
            <Typography
              color={muted ? disabledAutomationColors.value : undefined}
              component="div"
              sx={
                muted
                  ? {
                      '& .MuiTypography-root, & span': {
                        color: disabledAutomationColors.value,
                      },
                    }
                  : undefined
              }
              variant="body2"
            >
              {summary}
            </Typography>
            {editable && (
              <IconButton
                className="section-edit-button"
                onClick={handleEditStart}
                size="small"
                sx={{ flexShrink: 0 }}
              >
                <Icon icon={faPencil} size="tiny" />
              </IconButton>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};
